import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { t } from '../stores/languageStore';
import { userApi } from '../utils/api';
import toast from 'react-hot-toast';

const Register: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const { register } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (registeredEmail) {
      const timer = setTimeout(() => {
        navigate('/login');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [registeredEmail, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim() || !email.trim() || !password.trim()) {
      toast.error(t('register.fillRequired'));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t('register.passwordMismatch'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('register.passwordTooShort'));
      return;
    }

    try {
      setLoading(true);
      const success = await register(username, email, password);
      if (success) {
        setRegisteredEmail(email);
        toast.success(t('register.success'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('register.failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await userApi.register({ username, email, password });
      toast.success(t('register.resendSuccess'));
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('register.sendFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (registeredEmail) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="card p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('register.verificationEmailSent')}</h2>
          <p className="text-gray-600 mb-2">
            {t('register.sentVerificationEmail').replace('{email}', registeredEmail)}
          </p>
          <p className="text-gray-600 mb-6">
            {t('register.checkEmail')}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            {t('register.willRedirect').replace('{seconds}', '3')}
          </p>
          <div className="space-y-3">
            <Link to="/login" className="btn btn-primary w-full">
              {t('register.backToLogin')}
            </Link>
            <button
              onClick={handleResendVerification}
              disabled={loading}
              className="text-blue-600 hover:text-blue-700 text-sm"
            >
              {loading ? t('register.sending') : t('register.resendVerification')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="card p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-6">
          {t('register.title')}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('register.username')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="input"
              placeholder={t('register.usernameTip')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('register.email')} <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder={t('register.enterEmail')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('register.password')} <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder={t('register.passwordTip')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('register.confirmPassword')} <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              placeholder={t('register.confirmPasswordPlaceholder')}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary w-full"
          >
            {loading ? t('register.registering') : t('register.title')}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          {t('register.hasAccount')}{' '}
          <Link to="/login" className="link font-medium">
            {t('register.loginNow')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;